/**
 * OnboardPage — 10-step wizard, Group 1 (Steps 1-3) shipped in this turn.
 *
 *   1. Restaurant identity  (name, address, timezone)
 *   2. Hours & AI greeting  (with live preview)
 *   3. Table plan           (total tables count — enables the live board)
 *
 * Data persists via existing PATCH /api/settings — no new backend endpoints
 * needed for Steps 1-3. Progress is tracked in `businesses.onboarding_step`
 * (added to SettingsIn) so a returning user resumes where they left off.
 *
 * Steps 4-10 land in Groups 2 & 3.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, ArrowRight, ArrowLeft, House, ClockClockwise, ForkKnife, SkipForward } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import TwilioRequirementCallout from "../components/TwilioRequirementCallout";

const STEPS = [
  { key: "identity", title: "Your restaurant",     icon: House,            group: 1 },
  { key: "hours",    title: "Hours & greeting",    icon: ClockClockwise,   group: 1 },
  { key: "tables",   title: "Your tables",         icon: ForkKnife,        group: 1 },
  { key: "phone",    title: "Phone number",        icon: House,            group: 2, link: "/dashboard/voice",  desc: "Point a Twilio number here (buy new, port existing, or forward). Auto-wires webhooks." },
  { key: "sms",      title: "SMS compliance",      icon: House,            group: 2, link: "/dashboard/a2p",    desc: "Register your brand with US carriers so texts don't get blocked. Takes 5 min; carrier review 3-7 days." },
  { key: "template", title: "Ready-text preview",  icon: House,            group: 2, link: "/dashboard/settings", desc: "Approve the text guests get when their table is ready. Personalize the tone if you want." },
  { key: "voice",    title: "AI voice script",     icon: House,            group: 3, link: "/dashboard/voice",  desc: "Read the greeting your AI will say on every call. Edit it if it doesn't sound like you." },
  { key: "pin",      title: "Manager PIN",         icon: House,            group: 3, link: "/dashboard/settings", desc: "Optional 4-digit PIN that gates Admin & Settings. Skip if it's just you." },
  { key: "plan",     title: "Pick a plan",         icon: House,            group: 3, link: "/pricing",          desc: "Free 14-day trial with all add-ons. Pick the tier you'll grow into — or stay on trial." },
  { key: "test",     title: "One-click test",      icon: House,            group: 3, testCall: true,            desc: "Send yourself a real test call. Hear your AI live, prove SMS delivers. This is the moment it becomes real." },
];

const TIMEZONES = [
  ["America/New_York", "Eastern"], ["America/Chicago", "Central"], ["America/Denver", "Mountain"],
  ["America/Phoenix", "Arizona"], ["America/Los_Angeles", "Pacific"], ["America/Anchorage", "Alaska"],
  ["Pacific/Honolulu", "Hawaii"], ["America/Puerto_Rico", "Atlantic"], ["UTC", "UTC"],
];

export default function OnboardPage() {
  const { business, setBusiness } = useAuth();
  const nav = useNavigate();
  const initialStep = business?.onboarding_step ?? 0;
  const [step, setStep] = useState(Math.min(initialStep, STEPS.length - 1));
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    business_name: business?.business_name || "",
    address: business?.address || "",
    timezone: business?.timezone || "America/New_York",
    hours: business?.hours || "",
    ai_greeting: business?.ai_greeting || "",
    tables_count: business?.tables_count || "",
  });

  useEffect(() => {
    if (business?.onboarding_completed_at) nav("/dashboard");
  }, [business, nav]);

  const patchAndAdvance = async (fields, nextStep) => {
    setSaving(true);
    try {
      const payload = { ...fields, onboarding_step: nextStep };
      if (nextStep >= STEPS.length) payload.onboarding_completed_at = new Date().toISOString();
      const r = await api.patch("/settings", payload);
      setBusiness && setBusiness(r.data);
      if (nextStep >= STEPS.length) {
        toast.success("Setup complete — welcome to your dashboard!");
        nav("/dashboard");
      } else {
        setStep(nextStep);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const skipRest = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/settings", { onboarding_skipped_at: new Date().toISOString() });
      setBusiness && setBusiness(r.data);
      nav("/dashboard");
    } catch (e) {
      toast.error("Skip failed");
    }
    setSaving(false);
  };

  // "Save what I have, come back later" — persists whatever's filled in the
  // current step (blanks allowed) and returns the user to the dashboard.
  // Onboarding_step stays put, so the wizard resumes right where they left off
  // the next time they open it.
  const saveDraftAndExit = async () => {
    setSaving(true);
    try {
      const fields =
        step === 0 ? { business_name: form.business_name, address: form.address, timezone: form.timezone } :
        step === 1 ? { hours: form.hours, ai_greeting: form.ai_greeting } :
        step === 2 ? { tables_count: form.tables_count === "" ? null : parseInt(form.tables_count) } :
                     {};
      // Strip empty strings so we don't overwrite existing data with blanks.
      const payload = Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== "" && v !== null && v !== undefined));
      if (Object.keys(payload).length) {
        const r = await api.patch("/settings", payload);
        setBusiness && setBusiness(r.data);
      }
      toast.success("Saved what you have. We'll pick up right here when you come back.");
      nav("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const current = STEPS[step];
  const progressPct = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-[#F7F6F2] py-8 px-4" data-testid="onboard-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="overline text-[#D96C5B] mb-2">Setting up · {step + 1} of {STEPS.length}</div>
          <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-[#121914] mb-2">Let's front-load this once.</h1>
          <p className="text-sm text-[#5C6B61] max-w-md mx-auto">Fill everything now — then every shift after runs itself. About 10 minutes total. Skip any step; come back later.</p>
        </div>

        {/* Reassurance banner — the biggest driver of onboarding dropoff is
            people staring at a field like EIN they don't have handy and just
            closing the tab. Every field here is optional; make that loud. */}
        <div className="mb-6 p-3 rounded-lg bg-[#C2D1C1]/30 border border-[#1E3A2F]/20 text-xs text-[#121914] leading-snug" data-testid="onb-reassurance">
          <b>Missing an EIN, tax ID, address, or anything else?</b> Save what you have — we'll remember every field and pick up exactly where you left off. Nothing here has to be filled in one sitting.
        </div>

        {step === 0 && (
          <div className="mb-6" data-testid="onboard-twilio-callout">
            <TwilioRequirementCallout variant="full" />
          </div>
        )}

        {/* Progress bar */}
        <div className="h-1.5 bg-[#E1DFD5] rounded-full mb-8 overflow-hidden">
          <div className="h-full bg-[#1E3A2F] transition-all duration-500" style={{ width: `${progressPct}%` }} data-testid="onboard-progress" />
        </div>

        {/* Step body */}
        <div className="paper-card p-6 sm:p-8 mb-4" data-testid={`step-${current.key}`}>
          <div className="flex items-center gap-3 mb-1">
            <current.icon size={24} weight="fill" color="#1E3A2F" />
            <div className="overline text-[#5C6B61]">Step {step + 1}</div>
          </div>
          <h2 className="text-2xl font-heading font-extrabold text-[#121914] mb-6">{current.title}</h2>

          {step === 0 && (
            <div className="space-y-4">
              <p className="text-sm text-[#5C6B61] -mt-3 mb-2">Your restaurant's identity. Guests see the name; the AI uses it in every reply.</p>
              <Field label="Restaurant name" value={form.business_name} onChange={(v) => setForm({ ...form, business_name: v })} testid="onb-name" required />
              <Field label="Street address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="onb-address" placeholder="123 Main St, City, ST" />
              <div>
                <label className="overline block mb-1">Local timezone</label>
                <select
                  value={form.timezone}
                  onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                  className="input-field"
                  data-testid="onb-timezone"
                >
                  {TIMEZONES.map(([v, n]) => <option key={v} value={v}>{n} — {v}</option>)}
                </select>
                <p className="text-[10px] text-[#5C6B61] mt-1">The AI parses "8pm tonight" in this timezone.</p>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-[#5C6B61] -mt-3 mb-2">The AI greets every caller with this line. Guests hear it in the first 2 seconds — worth getting right.</p>
              <Field label="Hours (free text)" value={form.hours} onChange={(v) => setForm({ ...form, hours: v })} testid="onb-hours" placeholder="Tue–Sun 5pm–10pm, closed Mondays" />
              <div>
                <label className="overline block mb-1">AI greeting</label>
                <textarea
                  value={form.ai_greeting}
                  onChange={(e) => setForm({ ...form, ai_greeting: e.target.value })}
                  className="input-field min-h-[80px]"
                  data-testid="onb-greeting"
                  placeholder={`Hi, thanks for calling ${form.business_name || "our restaurant"}! I can help you join the waitlist or answer quick questions — how can I help?`}
                />
                {form.ai_greeting && (
                  <div className="mt-3 p-3 rounded-lg bg-[#C2D1C1]/30 border border-[#1E3A2F]/15" data-testid="greeting-preview">
                    <div className="overline text-[#1E3A2F] mb-1">Live preview</div>
                    <div className="text-sm text-[#121914] italic">"{form.ai_greeting}"</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-[#5C6B61] -mt-3 mb-2">The number of physical tables. Enables your live table board and the rolling wait-time estimator.</p>
              <Field label="Total tables" type="number" value={form.tables_count} onChange={(v) => setForm({ ...form, tables_count: v })} testid="onb-tables" placeholder="e.g. 18" />
              <div className="p-4 rounded-lg bg-[#F5C543]/15 border border-[#F5C543]/40 text-xs text-[#121914] leading-snug">
                <b>Why this matters:</b> Every time your host taps "seat" or "clear" during service, we learn your real turn-time. In 2 weeks the estimator is more accurate than a static number you'd type in — and walk-ins get counted automatically.
              </div>
            </div>
          )}

          {step > 2 && (
            <div className="text-center py-10">
              <div className="overline text-[#D96C5B] mb-2">Coming next</div>
              <p className="text-sm text-[#5C6B61] max-w-sm mx-auto mb-6">
                Steps 4-10 (Twilio phone number, SMS compliance, voice script, plan, one-click test) ship in the next release. Skip to the dashboard for now — we'll email you when they're ready.
              </p>
              <button onClick={skipRest} disabled={saving} className="btn-primary" data-testid="finish-early">
                Skip to dashboard <ArrowRight size={14} className="inline ml-1" />
              </button>
            </div>
          )}
        </div>

        {/* Footer nav — visible on all steps */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <button
            disabled={step === 0 || saving}
            onClick={() => setStep(step - 1)}
            className="text-sm font-semibold text-[#5C6B61] hover:text-[#121914] px-4 py-2 disabled:opacity-30 inline-flex items-center gap-1"
            data-testid="onb-back"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={saveDraftAndExit}
              disabled={saving}
              className="text-xs font-semibold text-[#1E3A2F] underline hover:text-[#121914] inline-flex items-center gap-1"
              data-testid="onb-save-later"
            >
              Save & finish later
            </button>
            <button
              onClick={skipRest}
              disabled={saving}
              className="text-xs font-semibold text-[#5C6B61] hover:text-[#121914] inline-flex items-center gap-1"
              data-testid="onb-skip-all"
            >
              <SkipForward size={12} /> Skip setup entirely
            </button>
            {step <= 2 && (
              <button
                disabled={saving || (step === 0 && !form.business_name)}
                onClick={() => {
                  const fields =
                    step === 0 ? { business_name: form.business_name, address: form.address, timezone: form.timezone } :
                    step === 1 ? { hours: form.hours, ai_greeting: form.ai_greeting } :
                                { tables_count: form.tables_count === "" ? null : parseInt(form.tables_count) };
                  patchAndAdvance(fields, step + 1);
                }}
                className="btn-primary"
                data-testid="onb-next"
              >
                {saving ? "Saving…" : "Save & continue"}
                <ArrowRight size={14} className="inline ml-1" />
              </button>
            )}
            {step > 2 && <span />}
          </div>
        </div>

        {/* Steps overview */}
        <div className="mt-8 paper-card p-4 opacity-80">
          <div className="overline text-[#5C6B61] mb-2">All setup steps</div>
          <div className="grid grid-cols-2 gap-1.5 text-xs">
            {STEPS.map((s, i) => (
              <div key={s.key} className={`flex items-center gap-2 ${i === step ? "text-[#1E3A2F] font-semibold" : i < step ? "text-[#5C6B61]" : "text-[#5C6B61]/60"}`}>
                {i < step ? <CheckCircle size={12} weight="fill" color="#1E3A2F" /> : <span className="w-3 h-3 rounded-full border border-current inline-block" />}
                {s.title}{s.comingSoon && <span className="text-[9px] text-[#D96C5B]">(soon)</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, testid, required, placeholder, type = "text" }) => (
  <div>
    <label className="overline block mb-1">{label}{required && <span className="text-[#D96C5B] ml-1">*</span>}</label>
    <input type={type} className="input-field" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder} required={required} />
  </div>
);
